import roomApi from '@/api/room'
import ws from '@/api/ws'

const state = {
  room: null,
  isOnline: false,
  wsConnected: false,
  messages: [],
  gameMode: 'single', // 'single' or 'online'
  dealPayload: null // 缓存 DEAL 消息，避免页面切换时丢失
}

const getters = {
  isInRoom: state => !!state.room,
  isOwner: (state, getters, rootState) => {
    return state.room && state.room.ownerId === rootState.playerId
  },
  onlinePlayers: state => {
    return state.room ? state.room.players.filter(p => p.online !== false) : []
  }
}

const mutations = {
  SET_ROOM(state, room) {
    state.room = room
    state.messages = []
  },
  SET_GAME_MODE(state, mode) {
    state.gameMode = mode
  },
  SET_WS_CONNECTED(state, connected) {
    state.wsConnected = connected
  },
  UPDATE_ROOM(state, room) {
    state.room = room
  },
  ADD_MESSAGE(state, msg) {
    state.messages.push(msg)
  },
  SET_CHAT_MESSAGES(state, list) {
    const incoming = Array.isArray(list) ? list : []
    const merged = new Map()
    incoming.forEach(item => {
      if (item && item.id) merged.set(item.id, item)
    })
    ;(state.messages || []).forEach(item => {
      if (item && item.id && !merged.has(item.id)) merged.set(item.id, item)
    })
    state.messages = Array.from(merged.values())
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
      .slice(-100)
  },
  ADD_CHAT_MESSAGE(state, msg) {
    if (!msg || !msg.id) return
    if (state.messages.some(item => item.id === msg.id)) return
    state.messages.push(msg)
    if (state.messages.length > 100) {
      state.messages.splice(0, state.messages.length - 100)
    }
  },
  CLEAR_ROOM(state) {
    state.room = null
    state.messages = []
    state.wsConnected = false
    state.dealPayload = null
  },
  SET_DEAL_PAYLOAD(state, payload) {
    state.dealPayload = payload
  }
}

const actions = {
  async createRoom({ commit, rootState }, config) {
    const playerId = rootState.playerId || ('p-' + Math.random().toString(36).substr(2, 9))
    const playerName = rootState.playerName || '玩家'
    commit('SET_GAME_MODE', 'online')
    const res = await roomApi.createRoom({
      playerId,
      playerName,
      maxPlayers: config.playerCount || 2,
      baseScore: config.baseScore || 1,
      arrangeTime: config.arrangeTime || 60,
      enableRandomSeat: config.enableRandomSeat !== false,
      enableIpLimit: config.enableIpLimit !== false,
      enableSpecialHands: config.enableSpecialHands !== false,
      enableHomeRun: config.enableHomeRun !== false,
      allowOddCards: config.allowOddCards === true,
      allowFouled: config.allowFouled === true,
      enableHorse: config.enableHorse === true,
      horseSuit: config.horseSuit || 'hearts',
      horseRank: config.horseRank || 1,
      enableYucai: config.enableYucai === true,
      yucaiScore: config.yucaiScore || 0,
      allowRoomRate: config.allowRoomRate === true,
      roomRatePercent: config.roomRatePercent || 2,
      dragonScore: config.dragonScore || 15,
      allBigScore: config.allBigScore || 10,
      allSmallScore: config.allSmallScore || 10,
      sixPairsHalfScore: config.sixPairsHalfScore || 7,
      threeFlushScore: config.threeFlushScore || 7,
      threeStraightScore: config.threeStraightScore || 7,
      sweepScoreType: config.sweepScoreType || 'score',
      sweepScoreValue: config.sweepScoreValue || 2,
      dealMode: config.dealMode || 'single',
      ghostMode: config.ghostMode || 0,
      roundCount: config.rounds || config.roundCount || 20,
      limitType: config.limitType || 'round',
      durationMinutes: config.durationMinutes || 0,
      extraSuits: config.extraSuits || [],
      rowSpecialScores: config.rowSpecialScores || {
        frontThreeKind: 3,
        middleFullHouse: 2,
        middleFourKind: 8,
        middleStraightFlush: 10,
        middleFiveKind: 20,
        backFourKind: 4,
        backStraightFlush: 5,
        backFiveKind: 10
      },
      diamondCost: config.diamondCost || 0
    })
    if (res.success) {
      commit('SET_ROOM', res.data)
      return res.data
    }
    const createError = new Error(res.message || '创建房间失败')
    createError.handled = !!res.handled
    throw createError
  },

  async joinRoom({ commit, rootState }, roomId) {
    const playerId = rootState.playerId || ('p-' + Math.random().toString(36).substr(2, 9))
    const playerName = rootState.playerName || '玩家'
    commit('SET_GAME_MODE', 'online')
    const res = await roomApi.joinRoom(roomId, { playerId, playerName })
    if (res.success) {
      commit('SET_ROOM', res.data)
      return res.data
    }
    const joinError = new Error(res.message || '加入房间失败')
    joinError.handled = !!res.handled
    throw joinError
  },

  async leaveRoom({ commit, state, rootState }, options = {}) {
    const silent = !!(options && options.silent)
    if (state.room) {
      await roomApi.leaveRoom(state.room.id, { playerId: rootState.playerId }, silent)
    }
    ws.disconnect()
    commit('CLEAR_ROOM')
  },

  connectWS({ commit, state, rootState }) {
    const player = state.room.players.find(p => p.id === rootState.playerId)
    if (!player) return
    ws.connect(player.id, state.room.id, () => {
      commit('SET_WS_CONNECTED', true)
    }, (err) => {
      console.error('WS error:', err)
      commit('SET_WS_CONNECTED', false)
    })
  },

  sendConfirmLayout({ state, rootState }, payload) {
    const playerId = rootState.playerId
    if (!playerId || !state.room) return
    const layout = payload && payload.layout ? payload.layout : payload
    const reportSpecial = !!(payload && payload.reportSpecial)
    ws.send(`/app/room/${state.room.id}/confirm`, {
      playerId: playerId,
      layout,
      reportSpecial
    })
  },

  sendStartGame({ state, rootState }) {
    const playerId = rootState.playerId
    if (!playerId || !state.room) return
    ws.send(`/app/room/${state.room.id}/start`, {
      playerId: playerId
    })
  },

  sendNextRound({ state, rootState }) {
    const playerId = rootState.playerId
    if (!playerId || !state.room) return
    ws.send(`/app/room/${state.room.id}/next`, {
      playerId: playerId
    })
  },

  async setReady({ commit, state, rootState }, ready) {
    const playerId = rootState.playerId
    if (!playerId || !state.room) return
    const res = await roomApi.setReady(state.room.id, { playerId: playerId, ready })
    if (res.success) {
      commit('UPDATE_ROOM', res.data)
      return res.data
    }
    const readyError = new Error(res.message || '设置准备状态失败')
    readyError.handled = !!res.handled
    throw readyError
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
